import React, { createContext } from 'react';
import cn from 'classnames';
import _get from 'lodash/get';
import styles from './index.module.scss';

import { If } from 'utils';
import { PageErrorUi, UiKit } from 'components';
import {
  LoaderSkeleton,
  Header,
  KeepItemBanner,
  ProductVariants,
  ServiceReason,
  ShopperComment,
  AttachedImages,
  StatusAlert,
  AppliedRules,
  Timeline,
  RetexNotes,
  Disposition,
  AmountBreakdown,
  Actions,
  CycleRetexes,
  ShopperGroupIncentive
} from './components';
import { LockedReason } from 'components/OrderServiceDetails/components';
import { useRetex, useRetexes } from './hooks';

export const RetexContext = createContext({
  retex: {},
  orderService: {},
  retexStatuses: {},
  approvalTypes: {},
  automatedRuleTypes: {},
  isApproved: false,
  isRejected: false,
  isCancelled: false,
  isAwatitingAtAdmin: false,
  isDisabled: false,
  showAction: false,
  onLoadData: () => {},
  onGotoRetex: () => {},
  onUpdateRetex: () => {},
  setIsDisabled: () => {},
  onUpdateOrderService: () => {}
});

const DEFAULT_ON_LOADED_CALLBACK = () => {};

export default function RetexDetails({
  osId = '',
  retexId = '',
  containerClassName = '',
  footerClassName = '',
  onGotoRetex = () => {},
  onLoaded = DEFAULT_ON_LOADED_CALLBACK
}) {
  const retexesData = useRetexes(osId);
  const { isError = false, isLoading = false, ...contextData } = useRetex(
    osId,
    retexId,
    onLoaded
  );

  const isLocked = _get(contextData.orderService, 'on_hold', false);
  const lockReason = _get(contextData.orderService, 'on_hold_reason', '');
  const lockReasonCode = _get(
    contextData.orderService,
    'on_hold_reason_code',
    ''
  );
  const wrapperClassName = cn(styles.container, containerClassName);

  return (
    <RetexContext.Provider
      value={{ onGotoRetex, ...retexesData, ...contextData }}
    >
      <PageErrorUi isError={isError} onReload={contextData.onLoadData}>
        <LoaderSkeleton isLoading={isLoading || !true}>
          <div className={wrapperClassName}>
            <UiKit.Grid gap="60">
              <UiKit.Grid templateColumns="1fr" gap="20">
                <Header />
                <KeepItemBanner />
                <ProductVariants />
                <ServiceReason />
                <ShopperComment />
                <ShopperGroupIncentive />
                <AttachedImages />
              </UiKit.Grid>
              <UiKit.Grid templateColumns="1fr" gap="32">
                <UiKit.Grid templateColumns="1fr" gap="20">
                  <UiKit.Grid
                    gap="20"
                    templateColumns="1fr auto"
                    className={styles.row}
                  >
                    <StatusAlert />
                    <CycleRetexes />
                  </UiKit.Grid>
                  <If test={contextData.showAction}>
                    <LockedReason
                      code={lockReasonCode}
                      reason={isLocked ? lockReason : null}
                    />
                  </If>
                  <UiKit.Grid templateColumns="1fr" gap="16">
                    <AppliedRules />
                    <Timeline />
                    <RetexNotes />
                  </UiKit.Grid>
                </UiKit.Grid>
                <Disposition />
                <AmountBreakdown />
              </UiKit.Grid>
            </UiKit.Grid>
            <Actions className={footerClassName} />
          </div>
        </LoaderSkeleton>
      </PageErrorUi>
    </RetexContext.Provider>
  );
}
